import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

interface Campaign {
  id: string;
  name: string;
  description: string;
  goal: number;
  raised: number;
  startDate: Date;
  endDate: Date;
  status: 'draft' | 'active' | 'completed' | 'archived';
  beneficiary: string;
  region: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Beneficiary {
  id: string;
  name: string;
  description: string;
  contactPerson: string;
  email: string;
  phone: string;
  website: string;
  region: string;
  verified: boolean;
}

interface RevenueConfig {
  id: string;
  transactionType: 'marketplace' | 'subscription' | 'event_buyin' | 'voice_pack' | 'motion_pack';
  percentage: number;
  enabled: boolean;
  campaignId?: string;
}

const CharityManagement: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'beneficiaries' | 'revenue' | 'audit'>('campaigns');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [revenueConfigs, setRevenueConfigs] = useState<RevenueConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editingBeneficiary, setEditingBeneficiary] = useState<Beneficiary | null>(null);

  // Campaign form state
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    goal: 0,
    startDate: '',
    endDate: '',
    status: 'draft' as Campaign['status'],
    beneficiary: '',
    region: ''
  });

  // Beneficiary form state
  const [beneficiaryForm, setBeneficiaryForm] = useState({
    name: '',
    description: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    region: '',
    verified: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCampaigns(),
        loadBeneficiaries(),
        loadRevenueConfigs()
      ]);
    } catch (error) {
      console.error('Error loading charity data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    const q = query(collection(db, 'charity_campaigns'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const campaignsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate(),
      endDate: doc.data().endDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as Campaign[];
    setCampaigns(campaignsData);
  };

  const loadBeneficiaries = async () => {
    const snapshot = await getDocs(collection(db, 'charity_beneficiaries'));
    const beneficiariesData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Beneficiary[];
    setBeneficiaries(beneficiariesData);
  };

  const loadRevenueConfigs = async () => {
    const snapshot = await getDocs(collection(db, 'charity_revenue_configs'));
    const configsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as RevenueConfig[];
    setRevenueConfigs(configsData);
  };

  const handleSaveCampaign = async () => {
    try {
      const campaignData = {
        ...campaignForm,
        startDate: Timestamp.fromDate(new Date(campaignForm.startDate)),
        endDate: Timestamp.fromDate(new Date(campaignForm.endDate)),
        raised: editingCampaign?.raised || 0,
        updatedAt: Timestamp.now()
      };

      if (editingCampaign) {
        await updateDoc(doc(db, 'charity_campaigns', editingCampaign.id), campaignData);
      } else {
        await addDoc(collection(db, 'charity_campaigns'), {
          ...campaignData,
          createdAt: Timestamp.now()
        });
      }

      setShowCampaignModal(false);
      setEditingCampaign(null);
      resetCampaignForm();
      loadCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
    }
  };

  const handleSaveBeneficiary = async () => {
    try {
      if (editingBeneficiary) {
        await updateDoc(doc(db, 'charity_beneficiaries', editingBeneficiary.id), beneficiaryForm);
      } else {
        await addDoc(collection(db, 'charity_beneficiaries'), beneficiaryForm);
      }

      setShowBeneficiaryModal(false);
      setEditingBeneficiary(null);
      resetBeneficiaryForm();
      loadBeneficiaries();
    } catch (error) {
      console.error('Error saving beneficiary:', error);
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (window.confirm(t('charity.confirmDeleteCampaign'))) {
      try {
        await deleteDoc(doc(db, 'charity_campaigns', id));
        loadCampaigns();
      } catch (error) {
        console.error('Error deleting campaign:', error);
      }
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      description: campaign.description,
      goal: campaign.goal,
      startDate: campaign.startDate.toISOString().split('T')[0],
      endDate: campaign.endDate.toISOString().split('T')[0],
      status: campaign.status,
      beneficiary: campaign.beneficiary,
      region: campaign.region
    });
    setShowCampaignModal(true);
  };

  const handleEditBeneficiary = (beneficiary: Beneficiary) => {
    setEditingBeneficiary(beneficiary);
    setBeneficiaryForm({
      name: beneficiary.name,
      description: beneficiary.description,
      contactPerson: beneficiary.contactPerson,
      email: beneficiary.email,
      phone: beneficiary.phone,
      website: beneficiary.website,
      region: beneficiary.region,
      verified: beneficiary.verified
    });
    setShowBeneficiaryModal(true);
  };

  const handleDeleteBeneficiary = async (id: string) => {
    if (window.confirm(t('charity.confirmDeleteBeneficiary'))) {
      try {
        await deleteDoc(doc(db, 'charity_beneficiaries', id));
        loadBeneficiaries();
      } catch (error) {
        console.error('Error deleting beneficiary:', error);
      }
    }
  };

  const handleUpdateRevenueConfig = async (config: RevenueConfig) => {
    try {
      if (config.id) {
        // Update existing config
        await updateDoc(doc(db, 'charity_revenue_configs', config.id), {
          percentage: config.percentage,
          enabled: config.enabled
        });
      } else {
        // Create new config
        await addDoc(collection(db, 'charity_revenue_configs'), {
          transactionType: config.transactionType,
          percentage: config.percentage,
          enabled: config.enabled
        });
      }
      loadRevenueConfigs();
    } catch (error) {
      console.error('Error updating revenue config:', error);
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      goal: 0,
      startDate: '',
      endDate: '',
      status: 'draft',
      beneficiary: '',
      region: ''
    });
  };

  const resetBeneficiaryForm = () => {
    setBeneficiaryForm({
      name: '',
      description: '',
      contactPerson: '',
      email: '',
      phone: '',
      website: '',
      region: '',
      verified: false
    });
  };

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-500/20 text-gray-400';
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      case 'archived': return 'bg-gray-500/20 text-gray-500';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const calculateProgress = (raised: number, goal: number) => {
    return Math.min((raised / goal) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-4xl font-bold text-gradient-cyan mb-2">
            💝 {t('charity.title')}
          </h2>
          <p className="text-[#B8B8C3]">{t('charity.description')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#31383D] overflow-x-auto">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${
            activeTab === 'campaigns'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {t('charity.campaigns')}
        </button>
        <button
          onClick={() => setActiveTab('beneficiaries')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${
            activeTab === 'beneficiaries'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {t('charity.beneficiaries')}
        </button>
        <button
          onClick={() => setActiveTab('revenue')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${
            activeTab === 'revenue'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {t('charity.revenueConfig')}
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${
            activeTab === 'audit'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {t('charity.auditLog')}
        </button>
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold text-white">{t('charity.campaignManagement')}</h3>
            <button
              onClick={() => {
                resetCampaignForm();
                setEditingCampaign(null);
                setShowCampaignModal(true);
              }}
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('charity.newCampaign')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="card-dark p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-bold text-white">{campaign.name}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(campaign.status)}`}>
                    {t(`charity.status.${campaign.status}`)}
                  </span>
                </div>

                <p className="text-[#B8B8C3] text-sm mb-4 line-clamp-2">{campaign.description}</p>

                <div className="space-y-3 mb-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#B8B8C3]">{t('charity.progress')}</span>
                      <span className="text-white font-semibold">
                        {campaign.raised.toLocaleString()} / {campaign.goal.toLocaleString()} Ft
                      </span>
                    </div>
                    <div className="w-full bg-[#15191E] rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-green-500 to-cyan-500 h-2 rounded-full transition-all"
                        style={{ width: `${calculateProgress(campaign.raised, campaign.goal)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center text-sm text-[#B8B8C3]">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {campaign.region}
                  </div>

                  <div className="flex items-center text-sm text-[#B8B8C3]">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {campaign.startDate.toLocaleDateString()} - {campaign.endDate.toLocaleDateString()}
                  </div>

                  <div className="flex items-center text-sm text-[#B8B8C3]">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {campaign.beneficiary}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditCampaign(campaign)}
                    className="flex-1 btn-secondary text-sm"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteCampaign(campaign.id)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {campaigns.length === 0 && (
            <div className="card-dark p-12 text-center">
              <div className="text-6xl mb-4">💝</div>
              <h3 className="text-xl font-bold text-white mb-2">{t('charity.noCampaigns')}</h3>
              <p className="text-[#B8B8C3] mb-4">{t('charity.noCampaignsDesc')}</p>
              <button
                onClick={() => {
                  resetCampaignForm();
                  setShowCampaignModal(true);
                }}
                className="btn-primary"
              >
                {t('charity.createFirstCampaign')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Beneficiaries Tab */}
      {activeTab === 'beneficiaries' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold text-white">{t('charity.beneficiaryManagement')}</h3>
            <button
              onClick={() => {
                resetBeneficiaryForm();
                setEditingBeneficiary(null);
                setShowBeneficiaryModal(true);
              }}
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('charity.newBeneficiary')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {beneficiaries.map(beneficiary => (
              <div key={beneficiary.id} className="card-dark p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-bold text-white">{beneficiary.name}</h4>
                  {beneficiary.verified && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
                      ✓ {t('charity.verified')}
                    </span>
                  )}
                </div>

                <p className="text-[#B8B8C3] text-sm mb-4">{beneficiary.description}</p>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center text-[#B8B8C3]">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {beneficiary.contactPerson}
                  </div>
                  <div className="flex items-center text-[#B8B8C3]">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {beneficiary.email}
                  </div>
                  <div className="flex items-center text-[#B8B8C3]">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {beneficiary.phone}
                  </div>
                  {beneficiary.website && (
                    <div className="flex items-center text-cyan-400">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <a href={beneficiary.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {beneficiary.website}
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditBeneficiary(beneficiary)}
                    className="flex-1 btn-secondary text-sm"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteBeneficiary(beneficiary.id)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {beneficiaries.length === 0 && (
            <div className="card-dark p-12 text-center">
              <div className="text-6xl mb-4">🏢</div>
              <h3 className="text-xl font-bold text-white mb-2">{t('charity.noBeneficiaries')}</h3>
              <p className="text-[#B8B8C3] mb-4">{t('charity.noBeneficiariesDesc')}</p>
              <button
                onClick={() => {
                  resetBeneficiaryForm();
                  setShowBeneficiaryModal(true);
                }}
                className="btn-primary"
              >
                {t('charity.addFirstBeneficiary')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Revenue Configuration Tab */}
      {activeTab === 'revenue' && (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-4">{t('charity.revenueAllocation')}</h3>
          
          <div className="card-dark p-6">
            <p className="text-[#B8B8C3] mb-6">{t('charity.revenueAllocationDesc')}</p>
            
            <div className="space-y-4">
              {[
                { type: 'marketplace', label: t('charity.marketplace'), icon: '🛒' },
                { type: 'subscription', label: t('charity.subscription'), icon: '💳' },
                { type: 'event_buyin', label: t('charity.eventBuyin'), icon: '🎫' },
                { type: 'voice_pack', label: t('charity.voicePack'), icon: '🎤' },
                { type: 'motion_pack', label: t('charity.motionPack'), icon: '🎬' }
              ].map(({ type, label, icon }) => {
                const config = revenueConfigs.find(c => c.transactionType === type) || {
                  id: '',
                  transactionType: type as RevenueConfig['transactionType'],
                  percentage: 0,
                  enabled: false
                };

                return (
                  <div key={type} className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{icon}</span>
                      <div>
                        <p className="text-white font-semibold">{label}</p>
                        <p className="text-sm text-[#B8B8C3]">{config.percentage}% {t('charity.goesToCharity')}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={config.percentage}
                        onChange={(e) => {
                          const newConfig = { ...config, percentage: Number(e.target.value) };
                          handleUpdateRevenueConfig(newConfig);
                        }}
                        className="input-dark w-20 text-center"
                      />
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.enabled}
                          onChange={(e) => {
                            const newConfig = { ...config, enabled: e.target.checked };
                            handleUpdateRevenueConfig(newConfig);
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-4">{t('charity.transparencyAudit')}</h3>
          
          <div className="card-dark p-6">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <h4 className="text-xl font-bold text-white mb-2">{t('charity.auditComingSoon')}</h4>
              <p className="text-[#B8B8C3]">{t('charity.auditComingSoonDesc')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingCampaign ? t('charity.editCampaign') : t('charity.newCampaign')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.campaignName')}
                </label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('charity.campaignNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.description')}
                </label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  className="input-dark w-full"
                  rows={4}
                  placeholder={t('charity.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.goalAmount')}
                  </label>
                  <input
                    type="number"
                    value={campaignForm.goal}
                    onChange={(e) => setCampaignForm({ ...campaignForm, goal: Number(e.target.value) })}
                    className="input-dark w-full"
                    placeholder="100000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Állapot
                  </label>
                  <select
                    value={campaignForm.status}
                    onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value as Campaign['status'] })}
                    className="input-dark w-full"
                  >
                    <option value="draft">{t('charity.status.draft')}</option>
                    <option value="active">{t('charity.status.active')}</option>
                    <option value="completed">{t('charity.status.completed')}</option>
                    <option value="archived">{t('charity.status.archived')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.startDate')}
                  </label>
                  <input
                    type="date"
                    value={campaignForm.startDate}
                    onChange={(e) => setCampaignForm({ ...campaignForm, startDate: e.target.value })}
                    max={campaignForm.endDate || "9999-12-31"}
                    className="input-dark w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.endDate')}
                  </label>
                  <input
                    type="date"
                    value={campaignForm.endDate}
                    onChange={(e) => setCampaignForm({ ...campaignForm, endDate: e.target.value })}
                    min={campaignForm.startDate || undefined}
                    max="9999-12-31"
                    className="input-dark w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.beneficiary')}
                </label>
                <select
                  value={campaignForm.beneficiary}
                  onChange={(e) => setCampaignForm({ ...campaignForm, beneficiary: e.target.value })}
                  className="input-dark w-full"
                >
                  <option value="">{t('charity.selectBeneficiary')}</option>
                  {beneficiaries.map(b => (
                    <option key={b.id} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.region')}
                </label>
                <input
                  type="text"
                  value={campaignForm.region}
                  onChange={(e) => setCampaignForm({ ...campaignForm, region: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('charity.regionPlaceholder')}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveCampaign} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowCampaignModal(false);
                  setEditingCampaign(null);
                  resetCampaignForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beneficiary Modal */}
      {showBeneficiaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingBeneficiary ? t('charity.editBeneficiary') : t('charity.newBeneficiary')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.organizationName')}
                </label>
                <input
                  type="text"
                  value={beneficiaryForm.name}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('charity.organizationNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.description')}
                </label>
                <textarea
                  value={beneficiaryForm.description}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder={t('charity.organizationDescPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.contactPerson')}
                  </label>
                  <input
                    type="text"
                    value={beneficiaryForm.contactPerson}
                    onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, contactPerson: e.target.value })}
                    className="input-dark w-full"
                    placeholder={t('charity.contactPersonPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.email')}
                  </label>
                  <input
                    type="email"
                    value={beneficiaryForm.email}
                    onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, email: e.target.value })}
                    className="input-dark w-full"
                    placeholder="contact@organization.org"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.phone')}
                  </label>
                  <input
                    type="tel"
                    value={beneficiaryForm.phone}
                    onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, phone: e.target.value })}
                    className="input-dark w-full"
                    placeholder="+36 20 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('charity.website')}
                  </label>
                  <input
                    type="url"
                    value={beneficiaryForm.website}
                    onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, website: e.target.value })}
                    className="input-dark w-full"
                    placeholder="https://organization.org"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('charity.region')}
                </label>
                <input
                  type="text"
                  value={beneficiaryForm.region}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, region: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('charity.regionPlaceholder')}
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="verified"
                  checked={beneficiaryForm.verified}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, verified: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="verified" className="text-sm text-[#B8B8C3]">
                  {t('charity.verifiedOrganization')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveBeneficiary} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowBeneficiaryModal(false);
                  setEditingBeneficiary(null);
                  resetBeneficiaryForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharityManagement;
